/**
 * Route Access Control
 * --------------------
 * Unified access control for routes and module menu visibility.
 * Defines access rules that are shared between the router and module registry.
 *
 * Access Logic:
 * - requiredDomains: User must have AT LEAST ONE of these domains (OR logic)
 * - requiredRoles: User must have AT LEAST ONE of these roles (OR logic)
 * - Both must pass if both are specified (AND between domains and roles)
 * - requiresLogin: Must be authenticated (default true for protected routes)
 */

import {
  DomainId,
  SystemRoleId,
  CommonRoleId,
  GdcRoles,
  WorkflowSandboxRoles,
  ExperimentalRoles,
  ProductRegistryRoles,
} from './claim-id';
import type { UserAccessManager } from './user-access-manager';

/**
 * Access requirement definition
 */
export interface AccessRequirement {
  /** Route must have an authenticated user */
  requiresLogin?: boolean;
  /** User must have at least one of these domains */
  requiredDomains?: string[];
  /** User must have at least one of these roles */
  requiredRoles?: string[];
  /** User must have at least one of these claims */
  requiredClaims?: string[];
}

/**
 * Result of an access check
 */
export interface AccessCheckResult {
  allowed: boolean;
  reason?:
    'not-logged-in' | 'missing-domain' | 'missing-role' | 'missing-claim';
}

/**
 * Check if a user has access based on requirements
 */
export function checkAccess(
  access: UserAccessManager | undefined,
  isLoggedIn: boolean,
  requirement: AccessRequirement
): AccessCheckResult {
  // Check login requirement (defaults to true if any other requirements exist)
  const needsLogin =
    requirement.requiresLogin ??
    !!(
      requirement.requiredDomains?.length ||
      requirement.requiredRoles?.length ||
      requirement.requiredClaims?.length
    );

  if (needsLogin && !isLoggedIn) {
    return { allowed: false, reason: 'not-logged-in' };
  }

  if (!access) {
    // No access manager but login not required - allow
    if (!needsLogin) return { allowed: true };
    return { allowed: false, reason: 'not-logged-in' };
  }

  // Check domains (OR logic - need at least one)
  if (requirement.requiredDomains?.length) {
    const hasAnyDomain = requirement.requiredDomains.some((d) =>
      access.hasDomain(d)
    );
    if (!hasAnyDomain) {
      return { allowed: false, reason: 'missing-domain' };
    }
  }

  // Check roles (OR logic - need at least one)
  if (requirement.requiredRoles?.length) {
    const hasAnyRole = requirement.requiredRoles.some((r) => access.hasRole(r));
    if (!hasAnyRole) {
      return { allowed: false, reason: 'missing-role' };
    }
  }

  // Check claims (OR logic - need at least one)
  if (requirement.requiredClaims?.length) {
    const hasAnyClaim = requirement.requiredClaims.some((c) =>
      access.hasClaim(c)
    );
    if (!hasAnyClaim) {
      return { allowed: false, reason: 'missing-claim' };
    }
  }

  return { allowed: true };
}

// ============================================================================
// ROUTE ACCESS DEFINITIONS
// Define access requirements for each route pattern. Child routes inherit
// from parent patterns automatically.
// ============================================================================

/**
 * Route access rule with pattern matching
 */
export interface RouteAccessRule {
  /** Route pattern (supports wildcards like /module/gdc/*) */
  pattern: string;
  /** Access requirements */
  requirement: AccessRequirement;
  /** Optional description for documentation */
  description?: string;
}

/**
 * Central registry of route access rules.
 * Order matters - more specific patterns should come first.
 * Child routes inherit parent requirements unless explicitly overridden.
 */
export const ROUTE_ACCESS_RULES: RouteAccessRule[] = [
  // ============================================================================
  // PUBLIC ROUTES (no login required)
  // ============================================================================
  {
    pattern: '/',
    requirement: { requiresLogin: false },
    description: 'Home page - public',
  },
  {
    pattern: '/about',
    requirement: { requiresLogin: false },
    description: 'About page - public',
  },
  {
    pattern: '/login',
    requirement: { requiresLogin: false },
    description: 'Login page - public',
  },
  {
    pattern: '/notallowed',
    requirement: { requiresLogin: false },
    description: 'Access denied page - public',
  },

  // ============================================================================
  // SYSTEM TENANT ADMIN ROUTES
  // Requires AGWA.SystemTenant domain + admin/member/user admin role
  // ============================================================================
  {
    pattern: '/maintenance/companies',
    requirement: {
      requiredDomains: [DomainId.systemTenant],
      requiredRoles: [
        CommonRoleId.admin,
        SystemRoleId.memberAdmin,
        SystemRoleId.userAdmin,
      ],
    },
    description: 'Member management - AGWA staff only',
  },
  {
    pattern: '/maintenance/companies/*',
    requirement: {
      requiredDomains: [DomainId.systemTenant],
      requiredRoles: [
        CommonRoleId.admin,
        SystemRoleId.memberAdmin,
        SystemRoleId.userAdmin,
      ],
    },
    description: 'Member workspaces - AGWA staff only',
  },
  {
    pattern: '/maintenance/sites',
    requirement: {
      requiredDomains: [DomainId.systemTenant],
      requiredRoles: [
        CommonRoleId.admin,
        SystemRoleId.memberAdmin,
        SystemRoleId.userAdmin,
      ],
    },
    description: 'Site management - AGWA staff only',
  },
  {
    pattern: '/maintenance/sites/*',
    requirement: {
      requiredDomains: [DomainId.systemTenant],
      requiredRoles: [
        CommonRoleId.admin,
        SystemRoleId.memberAdmin,
        SystemRoleId.userAdmin,
      ],
    },
    description: 'Site workspaces - AGWA staff only',
  },
  {
    pattern: '/module/staff',
    requirement: {
      requiredDomains: [DomainId.systemTenant],
      requiredRoles: [CommonRoleId.admin, SystemRoleId.staffAdmin],
    },
    description: 'Staff management - AGWA staff admins only',
  },
  {
    pattern: '/module/audit-log',
    requirement: {
      requiredDomains: [DomainId.systemTenant],
      requiredRoles: [SystemRoleId.memberAdmin],
    },
    description: 'Security audit log - member admins only',
  },
  {
    pattern: '/module/user-activity',
    requirement: {
      requiredDomains: [DomainId.systemTenant],
      requiredRoles: [SystemRoleId.memberAdmin],
    },
    description: 'User activity dashboard - member admins only',
  },
  {
    pattern: '/module/email-audit',
    requirement: {
      requiredDomains: [DomainId.systemTenant],
      requiredRoles: [SystemRoleId.memberAdmin],
    },
    description: 'Email audit history - member admins only',
  },

  // ============================================================================
  // GLASS ACCREDITATION ROUTES
  // ============================================================================
  {
    pattern: '/module/glass-accreditation/settings',
    requirement: {
      requiredDomains: [DomainId.systemTenant],
      requiredRoles: [CommonRoleId.admin],
    },
    description: 'Accreditation settings - AGWA admins only',
  },
  {
    pattern: '/module/glass-accreditation/settings/*',
    requirement: {
      requiredDomains: [DomainId.systemTenant],
      requiredRoles: [CommonRoleId.admin],
    },
    description: 'Accreditation settings tabs - AGWA admins only',
  },
  {
    pattern: '/module/glass-accreditation/certificates',
    requirement: {
      requiredDomains: [
        DomainId.glassProcessor,
        DomainId.glassDataEntry,
        DomainId.glassAuditor,
      ],
    },
    description: 'Certificate list - any glass domain',
  },
  {
    // The saved views (expiring-soon, needs-audit, my-drafts, review-due,
    // expired-no-successor) are the SAME certificate list under a preset filter, so they carry the
    // same entitlement as the list itself. They had no rule until 2026-08-10, which meant
    // getRouteAccessRequirement fell through to its "requiresLogin only" default and offered them to
    // any signed-in user - including members of a non-glass type, whose data call the server then
    // refused with 403. The pattern must be a wildcard: the bare-list rule above is an EXACT match
    // and never covered these children.
    pattern: '/module/glass-accreditation/certificates/*',
    requirement: {
      requiredDomains: [
        DomainId.glassProcessor,
        DomainId.glassDataEntry,
        DomainId.glassAuditor,
      ],
    },
    description: 'Certificate saved views - any glass domain',
  },
  {
    // Same fall-through as the saved views above: no rule meant any signed-in user was offered it and
    // then met a 403. Any glass domain, matching what the server actually allows.
    pattern: '/module/glass-accreditation/companies',
    requirement: {
      requiredDomains: [
        DomainId.glassProcessor,
        DomainId.glassDataEntry,
        DomainId.glassAuditor,
      ],
    },
    description: 'Glass processor companies list - any glass domain',
  },
  {
    pattern: '/module/glass-accreditation/as2208/*',
    requirement: {
      requiredDomains: [
        DomainId.glassProcessor,
        DomainId.glassDataEntry,
        DomainId.glassAuditor,
      ],
    },
    description: 'AS2208 certificates - any glass domain',
  },
  {
    pattern: '/module/glass-accreditation/as4666/*',
    requirement: {
      requiredDomains: [
        DomainId.glassProcessor,
        DomainId.glassDataEntry,
        DomainId.glassAuditor,
      ],
    },
    description: 'AS4666 certificates - any glass domain',
  },

  // ============================================================================
  // PRODUCT REGISTRY ROUTES
  // Note: requiredDomains uses OR logic - user needs ANY of the listed domains.
  // Prime.System.IntellectualProperty = the member's own domain, Product.DataEntry = the AGWA
  // staff domain that works on a member's behalf and reaches every register through it.
  //
  // THE SUPPLIER TYPE NO LONGER ADMITS ANYONE, and that is the point of D-127 rather than an
  // oversight. Prime.System.Supplier is now a classification a site wears; the register hangs off
  // the company's Prime System IP, held on a site the system creates, and the right to edit it is
  // an assignment to THAT site. Leaving the supplier here would have re-opened the model D-127
  // retired: working at a supplier site would again be, by itself, the right to open the module
  // (@PIP-EDIT-003 says it is not), and a member who had lost the assignment would still reach
  // the route. D-035's all-inclusive-by-site grant is superseded.
  //
  // EVERY RULE IN THIS BLOCK CARRIES THE SAME LIST, deliberately: the module root, the two lists
  // and the two work areas are one gate expressed five times, and a reader who corrects one and
  // not the others leaves a door open. Settings is the exception and always was - AGWA support
  // admins only, through Product.DataEntry.
  //
  // The first domain here was named Product.Registry until D-067 renamed it, and this file kept
  // the old code for weeks: hasDomain matches exactly, so every rule below was denying the module
  // to the very members who hold the domain. The MODULE and its ROLES keep the Product.Registry
  // string, which is why ProductRegistryRoles.* below is unchanged and correct.
  // ============================================================================
  {
    pattern: '/module/product-registry/settings',
    requirement: {
      requiredDomains: [DomainId.productDataEntry],
      requiredRoles: [ProductRegistryRoles.SupportAdmin],
    },
    description: 'Product Registry settings - support admins only',
  },
  {
    pattern: '/module/product-registry/settings/*',
    requirement: {
      requiredDomains: [DomainId.productDataEntry],
      requiredRoles: [ProductRegistryRoles.SupportAdmin],
    },
    description: 'Product Registry settings tabs - support admins only',
  },
  {
    pattern: '/module/product-registry',
    requirement: {
      requiredDomains: [
        DomainId.primeSystemIntellectualProperty,
        DomainId.productDataEntry,
      ],
      requiredRoles: [ProductRegistryRoles.Creator],
    },
    description: 'Product Registry root - creators only',
  },
  {
    pattern: '/module/product-registry/products',
    requirement: {
      requiredDomains: [
        DomainId.primeSystemIntellectualProperty,
        DomainId.productDataEntry,
      ],
      requiredRoles: [ProductRegistryRoles.Creator],
    },
    description: 'Product Registry products - creators only',
  },
  {
    pattern: '/module/product-registry/products/*',
    requirement: {
      requiredDomains: [
        DomainId.primeSystemIntellectualProperty,
        DomainId.productDataEntry,
      ],
      requiredRoles: [ProductRegistryRoles.Creator],
    },
    description: 'Product Registry product work area - creators only',
  },
  {
    pattern: '/module/product-registry/reports',
    requirement: {
      requiredDomains: [
        DomainId.primeSystemIntellectualProperty,
        DomainId.productDataEntry,
        DomainId.productAuditor,
      ],
      requiredRoles: [
        ProductRegistryRoles.Creator,
        ProductRegistryRoles.Auditor,
      ],
    },
    description:
      'Test reports root list - creators see own, auditors/data-entry see scoped',
  },
  {
    pattern: '/module/product-registry/reports/*',
    requirement: {
      requiredDomains: [
        DomainId.primeSystemIntellectualProperty,
        DomainId.productDataEntry,
        DomainId.productAuditor,
      ],
      requiredRoles: [
        ProductRegistryRoles.Creator,
        ProductRegistryRoles.Auditor,
      ],
    },
    description:
      'Test report editor - creators see own, auditors/data-entry see scoped',
  },

  // ============================================================================
  // GDC (CLOUD FILES) ROUTES - AGWA internal only
  // Requires any GDC role (granted via AGWA.Staff.Developer role set)
  // ============================================================================
  {
    pattern: '/module/gdc',
    requirement: {
      requiredRoles: [
        GdcRoles.Admin,
        GdcRoles.Creator,
        GdcRoles.Approver,
        GdcRoles.Consumer,
      ],
    },
    description: 'GDC document list - users with any GDC role',
  },
  {
    pattern: '/module/gdc/*',
    requirement: {
      requiredRoles: [
        GdcRoles.Admin,
        GdcRoles.Creator,
        GdcRoles.Approver,
        GdcRoles.Consumer,
      ],
    },
    description: 'GDC documents - users with any GDC role',
  },

  // ============================================================================
  // WORKFLOW SANDBOX
  // Requires Workflow.Sandbox.Creator role (granted to SystemTenant users)
  // ============================================================================
  {
    pattern: '/sandbox/workflows',
    requirement: {
      requiredRoles: [WorkflowSandboxRoles.Creator],
    },
    description: 'Workflow Sandbox list - users with Creator role',
  },
  {
    pattern: '/sandbox/workflows/*',
    requirement: {
      requiredRoles: [WorkflowSandboxRoles.Creator],
    },
    description: 'Workflow Sandbox editor - users with Creator role',
  },

  // ============================================================================
  // EXPERIMENTAL ROUTES
  // Requires Experimental.Access role (granted to SystemTenant users)
  // ============================================================================
  {
    pattern: '/experimental/prod-test-thumb-generator',
    requirement: {
      requiredRoles: [ExperimentalRoles.Access],
    },
    description:
      'Product Test Thumbnail Sandbox - AGWA staff with Experimental access',
  },
  {
    pattern: '/experimental/*',
    requirement: {
      requiredRoles: [ExperimentalRoles.Access],
    },
    description: 'Experimental tools - AGWA staff with Experimental access',
  },

  // ============================================================================
  // GENERIC PROTECTED ROUTES
  // ============================================================================
  {
    pattern: '/modules',
    requirement: { requiresLogin: true },
    description: 'Modules hub - logged in users',
  },
  {
    // Same requirement as the hub it stands beside: the menu lists only what the registry's own
    // visibility filter already allows, so the page itself needs nothing more than a session.
    pattern: '/menu',
    requirement: { requiresLogin: true },
    description: 'Workflow menu - logged in users',
  },
  {
    pattern: '/user/profile',
    requirement: { requiresLogin: true },
    description: 'User profile - logged in users',
  },
  {
    pattern: '/settings',
    requirement: { requiresLogin: true },
    description: 'Settings - logged in users',
  },
  {
    pattern: '/settings/*',
    requirement: { requiresLogin: true },
    description: 'Settings pages - logged in users',
  },
];

/**
 * Find the access requirement for a given route path.
 * Matches the most specific pattern first.
 */
export function getRouteAccessRequirement(
  path: string
): AccessRequirement | undefined {
  // Normalize path
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  // Try exact match first
  const exactMatch = ROUTE_ACCESS_RULES.find(
    (r) => r.pattern === normalizedPath
  );
  if (exactMatch) return exactMatch.requirement;

  // Try wildcard patterns (sorted by specificity - longer patterns first)
  const wildcardRules = ROUTE_ACCESS_RULES.filter((r) =>
    r.pattern.includes('*')
  ).sort((a, b) => b.pattern.length - a.pattern.length);

  for (const rule of wildcardRules) {
    const regexPattern = rule.pattern
      .replace(/\*/g, '.*')
      .replace(/\//g, '\\/');
    const regex = new RegExp(`^${regexPattern}$`);
    if (regex.test(normalizedPath)) {
      return rule.requirement;
    }
  }

  // No specific rule found - default to requiring login
  return { requiresLogin: true };
}

/**
 * Check if a user can access a specific route
 */
export function canAccessRoute(
  path: string,
  access: UserAccessManager | undefined,
  isLoggedIn: boolean
): AccessCheckResult {
  const requirement = getRouteAccessRequirement(path);
  if (!requirement) {
    return { allowed: true };
  }
  return checkAccess(access, isLoggedIn, requirement);
}
